import React, { useState, useEffect } from "react";
import {
  FiBookOpen,
  FiUsers,
  FiUser,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiInfo,
  FiLoader,
  FiCheckCircle,
  FiAlertCircle
} from "react-icons/fi";
import api from "../../../connector/api";

export default function ClassroomDetailTab({ classId, classData }) {
  const [classroom, setClassroom] = useState(classData || null);
  const [loading, setLoading] = useState(!classData);
  const [error, setError] = useState(null);

  // Fetch classroom detailed info if classData isn't pre-loaded
  useEffect(() => {
    if (classData) {
      setClassroom(classData);
      return;
    }

    const fetchClassroomDetails = async () => {
      if (!classId) return;
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(`academic/classroom/${classId}/`);
        setClassroom(response.data);
      } catch (err) {
        console.error("Error fetching classroom details:", err);
        setError("Failed to load classroom details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchClassroomDetails();
  }, [classId, classData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white border border-zinc-200/80 rounded-xl shadow-xs">
        <FiLoader className="w-6 h-6 text-zinc-400 animate-spin mb-2" />
        <p className="text-xs text-zinc-500 font-medium">Loading classroom details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-white border border-red-200 rounded-xl p-6 text-center shadow-xs">
        <FiAlertCircle className="w-8 h-8 text-red-500 mb-2" />
        <p className="text-xs font-semibold text-zinc-800">{error}</p>
      </div>
    );
  }

  if (!classroom) return null;

  return (
    <div className="space-y-4">
      {/* Overview Banner Card */}
      <div className="bg-white border border-zinc-200/80 rounded-xl p-5 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-600 shrink-0 border border-zinc-200/60 mt-0.5">
            <FiBookOpen className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-zinc-900 tracking-tight">
                {classroom.name || classroom.label || `Classroom #${classroom.id}`}
              </h2>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/60 px-2 py-0.5 rounded-full">
                <FiCheckCircle className="w-3 h-3" />
                Active
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">
              {classroom.description || "Comprehensive view of class details, assigned teachers, and schedule."}
            </p>
          </div>
        </div>

        {/* Action / Badge info */}
        {classroom.code && (
          <div className="shrink-0 flex items-center gap-2 self-start sm:self-center bg-zinc-50 border border-zinc-200/80 px-3 py-1.5 rounded-lg">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Class Code:</span>
            <span className="text-xs font-mono font-bold text-zinc-800">{classroom.code}</span>
          </div>
        )}
      </div>

      {/* Metrics & Metadata Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Grade / Section */}
        <div className="bg-white border border-zinc-200/80 rounded-lg p-3.5 flex items-center gap-3 shadow-xs">
          <div className="w-8 h-8 rounded-md bg-zinc-50 border border-zinc-200/60 flex items-center justify-center text-zinc-500 shrink-0">
            <FiInfo className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Grade / Level</p>
            <p className="text-xs font-bold text-zinc-800 truncate">
              {classroom.grade_level ? `Grade ${classroom.grade_level}` : classroom.category || "N/A"}
            </p>
          </div>
        </div>

        {/* Room Location */}
        <div className="bg-white border border-zinc-200/80 rounded-lg p-3.5 flex items-center gap-3 shadow-xs">
          <div className="w-8 h-8 rounded-md bg-zinc-50 border border-zinc-200/60 flex items-center justify-center text-zinc-500 shrink-0">
            <FiMapPin className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Room / Location</p>
            <p className="text-xs font-bold text-zinc-800 truncate">
              {classroom.room_number || classroom.location || "Main Building"}
            </p>
          </div>
        </div>

        {/* Enrolled Students */}
        <div className="bg-white border border-zinc-200/80 rounded-lg p-3.5 flex items-center gap-3 shadow-xs">
          <div className="w-8 h-8 rounded-md bg-zinc-50 border border-zinc-200/60 flex items-center justify-center text-zinc-500 shrink-0">
            <FiUsers className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Total Students</p>
            <p className="text-xs font-bold text-zinc-800 truncate">
              {classroom.student_count ?? classroom.capacity ?? "--"} Enrolled
            </p>
          </div>
        </div>

        {/* Primary Teacher */}
        <div className="bg-white border border-zinc-200/80 rounded-lg p-3.5 flex items-center gap-3 shadow-xs">
          <div className="w-8 h-8 rounded-md bg-zinc-50 border border-zinc-200/60 flex items-center justify-center text-zinc-500 shrink-0">
            <FiUser className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Home Teacher</p>
            <p className="text-xs font-bold text-zinc-800 truncate">
              {classroom.teacher_name || classroom.homeroom_teacher || "Unassigned"}
            </p>
          </div>
        </div>
      </div>

      {/* Main Workspace Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - General Details */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-zinc-200/80 rounded-xl p-4 shadow-xs">
            <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider border-b border-zinc-100 pb-2.5 mb-3">
              Classroom Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-zinc-400 block text-[11px] font-medium">Academic Year</span>
                <span className="text-zinc-800 font-semibold">{classroom.academic_year || "2025/2026"}</span>
              </div>
              <div>
                <span className="text-zinc-400 block text-[11px] font-medium">Current Term</span>
                <span className="text-zinc-800 font-semibold">{classroom.term || "Term 1"}</span>
              </div>
              <div>
                <span className="text-zinc-400 block text-[11px] font-medium">Department / Section</span>
                <span className="text-zinc-800 font-semibold">{classroom.department || "General Academic"}</span>
              </div>
              <div>
                <span className="text-zinc-400 block text-[11px] font-medium">Max Capacity</span>
                <span className="text-zinc-800 font-semibold">{classroom.max_capacity || "30 Students"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Schedule & Quick Stats */}
        <div className="space-y-4">
          <div className="bg-white border border-zinc-200/80 rounded-xl p-4 shadow-xs">
            <h3 className="text-xs font-bold text-zinc-900 uppercase tracking-wider border-b border-zinc-100 pb-2.5 mb-3 flex items-center justify-between">
              <span>Schedule Overview</span>
              <FiClock className="w-3.5 h-3.5 text-zinc-400" />
            </h3>
            <div className="space-y-2.5 text-xs">
              <div className="flex items-center justify-between py-1.5 border-b border-zinc-50">
                <span className="text-zinc-500 font-medium">Days</span>
                <span className="text-zinc-800 font-semibold">{classroom.schedule_days || "Mon - Fri"}</span>
              </div>
              <div className="flex items-center justify-between py-1.5 border-b border-zinc-50">
                <span className="text-zinc-500 font-medium">Class Hours</span>
                <span className="text-zinc-800 font-semibold">{classroom.schedule_time || "08:00 AM - 03:00 PM"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}